import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  doc,
  getDoc,
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  increment
} from 'firebase/firestore';

const COMMENTS_COLLECTION = 'comments';

/**
 * Create a new comment on an idea
 * @param {string} ideaId - The idea ID
 * @param {Object} commentData - The comment data
 * @returns {Promise<string>} - The ID of the created comment
 */
export const createComment = async (ideaId, commentData) => {
  try {
    const commentsRef = collection(db, COMMENTS_COLLECTION);
    const docRef = await addDoc(commentsRef, {
      ...commentData,
      ideaId,
      votes: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating comment:', error);
    throw new Error('Failed to create comment');
  }
};

/**
 * Get all comments for an idea
 * @param {string} ideaId - The idea ID
 * @returns {Promise<Array>} - Array of comments
 */
export const getComments = async (ideaId) => {
  try {
    const commentsRef = collection(db, COMMENTS_COLLECTION);
    
    // First, try to get all comments without ordering
    // We'll sort client-side to handle serverTimestamp() delays
    const q = query(
      commentsRef,
      where('ideaId', '==', ideaId)
    );

    const querySnapshot = await getDocs(q);
    const comments = [];
    
    querySnapshot.forEach((doc) => {
      comments.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort client-side by createdAt (handles null timestamps)
    comments.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || Date.now();
      const bTime = b.createdAt?.toMillis?.() || Date.now();
      return bTime - aTime; // Newest first
    });

    return comments;
  } catch (error) {
    console.error('Error getting comments:', error);
    throw new Error('Failed to fetch comments');
  }
};

/**
 * Get a single comment by ID
 * @param {string} commentId - The comment ID
 * @returns {Promise<Object>} - The comment data
 */
export const getComment = async (commentId) => {
  try {
    const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
    const commentSnap = await getDoc(commentRef);
    
    if (commentSnap.exists()) {
      return {
        id: commentSnap.id,
        ...commentSnap.data(),
      };
    } else {
      throw new Error('Comment not found');
    }
  } catch (error) {
    console.error('Error getting comment:', error);
    throw error;
  }
};

/**
 * Update a comment
 * @param {string} commentId - The comment ID
 * @param {Object} updates - The fields to update
 * @returns {Promise<void>}
 */
export const updateComment = async (commentId, updates) => {
  try {
    const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
    await updateDoc(commentRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating comment:', error);
    throw new Error('Failed to update comment');
  }
};

/**
 * Delete a comment
 * @param {string} commentId - The comment ID
 * @returns {Promise<void>}
 */
export const deleteComment = async (commentId) => {
  try {
    const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
    await deleteDoc(commentRef);
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw new Error('Failed to delete comment');
  }
};

/**
 * Vote on a comment (upvote/downvote)
 * @param {string} commentId - The comment ID
 * @param {number} value - Vote value (1 for upvote, -1 for downvote)
 * @returns {Promise<void>}
 */
export const voteComment = async (commentId, value) => {
  try {
    const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
    await updateDoc(commentRef, {
      votes: increment(value),
    });
  } catch (error) {
    console.error('Error voting on comment:', error);
    throw new Error('Failed to vote on comment');
  }
};

/**
 * Get comment count for an idea
 * @param {string} ideaId - The idea ID
 * @returns {Promise<number>} - Number of comments
 */
export const getCommentCount = async (ideaId) => {
  try {
    const comments = await getComments(ideaId);
    return comments.length;
  } catch (error) {
    console.error('Error getting comment count:', error);
    return 0;
  }
};

